import React, { FunctionComponent, useEffect, useMemo } from 'react';
import {
  ManageNetworkMapViewsQuery,
  ManageNetworkMapHeatmapQuery,
} from 'generated/graphql';
import { ApolloError } from '@apollo/client';
import { FeatureCollection } from 'geojson';
import { ToastError, ToastWarn } from 'utils';
import ManageNetworkMapView from './manage-network-map-view';

type ManageNetworkMapProView = ManageNetworkMapViewsQuery['proView'][number];
type ManageNetworkMapCity = ManageNetworkMapHeatmapQuery['city'][number];

type ManageNetworkMapContainerProps = {
  views: ManageNetworkMapProView[];
  cities: ManageNetworkMapCity[];
  isLoading: boolean;
  isHeatmapLoading: boolean;
  error?: ApolloError;
};

const mapCitiesToFeatureCollection = (
  cities: ManageNetworkMapCity[],
): FeatureCollection | undefined => {
  if (!cities || cities.length === 0) {
    return undefined;
  }

  const maxViewCount = Math.max(
    ...cities.map((city) => city.proViewsAggregate.aggregate?.count || 0),
  );

  return {
    type: 'FeatureCollection',
    features: cities.map((city) => {
      const count = city.proViewsAggregate.aggregate?.count || 0;
      return {
        type: 'Feature' as const,
        properties: {
          id: city.id,
          name: city.name,
          count,
          color: count
            ? `hsl(${(1 - count / maxViewCount) * 120}, 100%, 50%)`
            : 'hsl(0, 0%, 100%)',
          stroke: false,
        },
        geometry: city.area,
      };
    }),
  };
};

const ManageNetworkMapContainer: FunctionComponent<
  ManageNetworkMapContainerProps
> = ({ views, cities, isLoading, isHeatmapLoading, error }) => {
  useEffect(() => {
    if (error) {
      return ToastError(
        'Erreur lors du chargement des pros',
        'Notre base de données est peut-être en panne, veuillez réessayer plus tard.',
      );
    }

    if (!isLoading && views.length === 0) {
      return ToastWarn('Aucun pro trouvé', 'Essayer de modifier les filtres');
    }
  }, [views.length, error]);

  const cityFeatureCollection = useMemo(
    () => mapCitiesToFeatureCollection(cities),
    [cities],
  );

  return (
    <ManageNetworkMapView
      views={views}
      displayMapLoading={isLoading || isHeatmapLoading}
      cityFeatureCollection={cityFeatureCollection}
    />
  );
};

export default ManageNetworkMapContainer;
